import { registerSoundStopper } from "./sound-guard";

export const VOICE_SAMPLE_RATE = 24_000;
/** A little audio is gathered before the first piece plays, so a slow network does not stutter. */
const LEAD_SECONDS = 0.18;

export interface StreamedVoice {
  /** Resolves when the first piece starts playing. */
  started: Promise<void>;
  /** Resolves when the last piece has finished (or the voice was stopped). */
  finished: Promise<void>;
  /** Seconds of voice received so far; final once `finished` resolves. */
  duration: () => number;
  /** Seconds played since the voice started. */
  elapsed: () => number;
  stop: () => void;
}

/**
 * Plays Iris's voice while it is still being made: raw 16-bit PCM pieces are scheduled
 * back to back on the audio engine, so she starts speaking about a second after her reply.
 */
export function playStreamedVoice(response: Response, context: AudioContext, destination: AudioNode): StreamedVoice {
  const sources = new Set<AudioBufferSourceNode>();
  let stopped = false;
  let startedAt = -1;
  let nextTime = 0;
  let received = 0;
  let carry: Uint8Array | null = null;
  let markStarted: () => void = () => {};
  let markFinished: () => void = () => {};
  const started = new Promise<void>((resolve) => (markStarted = resolve));
  const finished = new Promise<void>((resolve) => (markFinished = resolve));
  let unregister = () => {};

  const stop = () => {
    if (stopped) return;
    stopped = true;
    for (const source of sources) {
      try {
        source.stop();
      } catch {
        /* already ended */
      }
    }
    sources.clear();
    unregister();
    markStarted();
    markFinished();
  };
  unregister = registerSoundStopper(stop);

  const schedule = (bytes: Uint8Array) => {
    // PCM pieces can split a 16-bit sample; keep the odd byte for the next piece.
    let data = bytes;
    if (carry) {
      data = new Uint8Array(carry.length + bytes.length);
      data.set(carry);
      data.set(bytes, carry.length);
      carry = null;
    }
    if (data.length % 2) {
      carry = data.slice(data.length - 1);
      data = data.slice(0, data.length - 1);
    }
    if (!data.length) return;
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    const samples = new Float32Array(data.length / 2);
    for (let index = 0; index < samples.length; index++) samples[index] = view.getInt16(index * 2, true) / 32768;
    const buffer = context.createBuffer(1, samples.length, VOICE_SAMPLE_RATE);
    buffer.copyToChannel(samples, 0);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(destination);
    const now = context.currentTime;
    if (startedAt < 0) {
      nextTime = now + LEAD_SECONDS;
      startedAt = nextTime;
      window.setTimeout(markStarted, LEAD_SECONDS * 1000);
    } else if (nextTime < now) nextTime = now + 0.02; // the network fell behind: a tiny pause, never an overlap
    source.start(nextTime);
    nextTime += buffer.duration;
    received += buffer.duration;
    sources.add(source);
    source.onended = () => sources.delete(source);
  };

  (async () => {
    try {
      const reader = response.body?.getReader();
      if (!reader) throw new Error("No voice stream.");
      for (;;) {
        const { done, value } = await reader.read();
        if (stopped) {
          void reader.cancel();
          return;
        }
        if (done) break;
        if (value?.length) schedule(value);
      }
    } catch {
      /* a broken stream ends the voice; the words stay on screen */
    }
    if (stopped) return;
    const remaining = Math.max(0, nextTime - context.currentTime);
    window.setTimeout(() => {
      unregister();
      markStarted();
      markFinished();
    }, remaining * 1000 + 30);
  })();

  return {
    started,
    finished,
    duration: () => received,
    elapsed: () => (startedAt < 0 ? 0 : Math.max(0, context.currentTime - startedAt)),
    stop,
  };
}
